import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { branchSummary } from '../../shared/branch-name';
import { umgebungsSchluessel } from '../../shared/umgebung-name';

export type TestRunEnvironment = 'TEST' | 'STAGING' | 'PRODUCTION' | 'LOCAL';

const KNOWN: readonly string[] = ['TEST', 'STAGING', 'PRODUCTION', 'LOCAL'];

/**
 * Umgebung eines Testlaufs als Risiko-Skala: Test neutral, Staging Vorstufe,
 * Production kritisch. `LOCAL` ist im Design-Bundle nicht gestaltet und
 * übernimmt die neutrale Variante.
 *
 * Neue Läufe tragen den Namen der Projekt-Umgebung, ältere den Code (NEX-2283).
 * Die Farbe folgt deshalb dem Vergleichsschlüssel: „Staging“ wie `STAGING`,
 * „Produktion“ wie `PRODUCTION`, „Lokal“ wie `LOCAL`; jeder andere Name bleibt neutral.
 *
 * Bestandsdaten tragen auch Werte außerhalb dieser vier (etwa
 * `local-postgresql-16-mocked-integrations`); die bleiben lesbar und fallen
 * ebenfalls auf die neutrale Variante zurück. Pipeline-Läufe tragen hier den
 * Branch (`ObservedRunPersister`) — `fix/NEX-1329-objektreferenz-...` sprengt
 * jede Spaltenbreite. In der Tabelle zaehlen davon zwei Dinge: die Art des
 * Branches und das Ticket dahinter — die Pill zeigt `fix NEX-1329` und reicht
 * den vollen Namen über `title` und einen optisch versteckten Text nach — ein
 * `aria-label` an einem `<span>` ohne Rolle ignorieren Screenreader (ARIA 1.2).
 * Nennt der Branch kein
 * Ticket, bleibt es beim Typ; ein Branch ohne Typ (`develop`) steht ganz da. Mit
 * `isBranch` tritt ausserdem an die Stelle des Umgebungspunktes ein
 * Verzweigungssymbol: derselbe Platz, aber ohne die Risiko-Lesart, die einer
 * Umgebung zusteht und einem Branch nicht.
 */
@Component({
  selector: 'app-umgebung-pill',
  standalone: true,
  imports: [TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @let label = labelKey() ? (labelKey()! | translate) : rawLabel();
    <span
      class="umgebung-pill"
      [attr.data-umgebung]="schluessel()"
      [attr.data-testid]="testId()"
      [attr.title]="fullValue() ?? label"
    >
      @if (isBranch()) {
        <span class="umgebung-pill__icon material-icons" aria-hidden="true">call_split</span>
      } @else {
        <span class="umgebung-pill__dot" aria-hidden="true"></span>
      }
      @if (fullValue()) {
        <span class="umgebung-pill__label" aria-hidden="true">{{ label }}</span>
        <span class="umgebung-pill__full">{{ fullValue() }}</span>
      } @else {
        <span class="umgebung-pill__label">{{ label }}</span>
      }
    </span>
  `,
  styles: `
    :host {
      display: inline-flex;
      max-width: 100%;
      min-width: 0;
    }

    .umgebung-pill {
      display: inline-flex;
      align-items: center;
      gap: 0.4375rem;
      padding: 0.3125rem 0.6875rem;
      border-radius: 999px;
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;
      box-sizing: border-box;
      max-width: 100%;
      min-width: 0;
      background: var(--to-neutral-bg);
      color: var(--to-neutral-fg);

      &[data-umgebung='STAGING'] {
        background: var(--to-warn-bg);
        color: var(--to-warn-fg);

        .umgebung-pill__dot {
          background: var(--to-warn-dot);
        }
      }

      &[data-umgebung='PRODUCTION'] {
        background: var(--to-red-bg);
        color: var(--to-red-fg);

        .umgebung-pill__dot {
          background: var(--to-red-dot);
        }
      }
    }

    .umgebung-pill__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .umgebung-pill__full {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      overflow: hidden;
      clip-path: inset(50%);
      white-space: nowrap;
    }

    // Laedt die Icon-Font nicht, rendert die Ligatur als Wort 'call_split' und
    // schoebe die Pill aus der Spalte. Erst display/overflow halten das
    // Kaestchen bei 0.75rem, Glyph hin oder her.
    .umgebung-pill__icon {
      display: inline-block;
      overflow: hidden;
      font-size: 0.75rem;
      width: 0.75rem;
      height: 0.75rem;
      flex-shrink: 0;
    }

    .umgebung-pill__dot {
      width: 0.375rem;
      height: 0.375rem;
      border-radius: 50%;
      flex-shrink: 0;
      background: var(--to-neutral-dot);
    }
  `,
})
export class UmgebungPillComponent {
  readonly umgebung = input.required<string>();

  /** Pipeline-Laeufe tragen hier einen Branch statt einer Umgebung (NEX-1332). */
  readonly isBranch = input(false);

  readonly normalized = computed(() => (this.umgebung() ?? '').toUpperCase());
  readonly schluessel = computed(() => umgebungsSchluessel(this.umgebung()));
  readonly testId = computed(() => `umgebung-pill-${this.normalized().toLowerCase()}`);

  private readonly known = computed(() => KNOWN.includes(this.normalized()));

  readonly labelKey = computed(() => (this.known() ? `testRun.environment.${this.normalized()}` : null));

  /** Unbekannte Werte werden roh gezeigt — besser als ein leeres Feld. */
  readonly rawLabel = computed(() => {
    const raw = this.umgebung() ?? '';
    return this.isBranch() ? branchSummary(raw) : raw;
  });

  /**
   * Rohwerte koennen auf jeder Spaltenbreite abgeschnitten werden — sei es vom
   * gekappten Prefix oder vom Ellipsis. Deshalb haengt der volle Wert immer an
   * ihnen; die vier bekannten Umgebungen stehen ohnehin ganz da.
   */
  readonly fullValue = computed(() => (this.known() ? null : (this.umgebung() ?? '')));
}
